import React, {useCallback, useRef} from 'react';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useSession} from '../firebase/UserProvider';
import ExpertsChatMessages from '../features/chat/chat-experts/components/ExpertsChatMessages';
import MessageComposer from './MessageComposer';
import UserAvatar from './UserAvatar';

/**
 * Floating chat window popup.
 * Uses REST-backed ExpertsChatMessages + MessageInput (not Yjs).
 *
 * Props:
 *   chat    — chat object { id, participants, participant_<uid>, name, title, ... }
 *   onClose — called when the × button is clicked
 *   onBack  — if provided, replaces × with a ← arrow (inline panel mode)
 */
export default function ChatWindow({chat, onClose, onBack}) {
    const chatId = chat?.id || null;
    const {user: currentUser} = useSession() || {};
    const addOptimisticRef = useRef(null);
    const handleMessagesReady = useCallback((fn) => { addOptimisticRef.current = fn; }, []);

    const parts = chat?.participants || [];
    const myUid = currentUser?.uid || currentUser?.id || null;
    const otherUid = parts.length === 2
        ? parts.find((p) => p !== myUid) || parts[0]
        : parts.find((p) => p !== myUid) || parts[0] || null;

    const participantMeta = otherUid ? (chat?.[`participant_${otherUid}`] || null) : null;
    const displayName = participantMeta?.displayName || chat?.name || chat?.title || 'Chat';
    const avatarUrl = participantMeta?.profilePicture || participantMeta?.headshot || null;
    const otherUserObj = otherUid
        ? {
            ...participantMeta,
            id: otherUid,
            uid: otherUid,
            userId: otherUid,
            displayName,
            profilePicture: avatarUrl,
            headshot: avatarUrl,
            hasAvatar: true,
        }
        : null;

    const handleOptimisticMessage = useCallback(
        (msg) => addOptimisticRef.current?.({...msg, userId: currentUser?.uid, userDisplayName: currentUser?.displayName}),
        [currentUser],
    );

    return (
        <Stack height="100%" overflow="hidden" direction="column">

            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                flexShrink: 0,
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden'}}>
                    {onBack && (
                        <IconButton size="small" onClick={onBack} aria-label="Back to chats" sx={{flexShrink: 0}}>
                            <ArrowBackIcon fontSize="small"/>
                        </IconButton>
                    )}
                    <UserAvatar
                        user={otherUserObj}
                        userId={otherUid}
                        sx={{width: 32, height: 32, fontSize: 14}}
                    />
                    <Typography sx={{fontWeight: 600, fontSize: 14, noWrap: true}}>
                        {displayName}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} aria-label="Close chat" sx={{flexShrink: 0, display: onBack ? 'none' : undefined}}>
                    <span style={{fontSize: 18, lineHeight: 1}}>×</span>
                </IconButton>
            </Box>

            {/* Messages — REST-backed, live via STOMP */}
            <ExpertsChatMessages chatId={chatId} fullHeight onReady={handleMessagesReady}/>

            {/* Input */}
            <MessageComposer
                elementName="OneOnOne"
                chatId={chatId}
                userId={currentUser?.uid}
                userDisplayName={currentUser?.displayName}
                fullHeight
                autoFocus
                placeholder="Type a message… (paste image to attach)"
                submitLabel="Send"
                onOptimisticMessage={handleOptimisticMessage}
            />

        </Stack>
    );
}
